import { router } from 'expo-router';
import { useEffect, useMemo } from 'react';

import { Header, client, queryClient, setHeaders, useQuery } from '@fhs/client';
import { getCurrentSession } from '@fhs-legacy/frontend/src/remote/auth';
import { LaunchDarklyFlag, useFlag } from '@fhs-legacy/frontend/src/state/launchdarkly';
import { ServiceMode } from '@fhs-legacy/frontend/src/state/service-mode';
import WebLocalStorage, { StorageKeys } from '@fhs-legacy/frontend/src/utils/local-storage';
import { Toast } from '@fhs-legacy/native-base';

import { CartOptions, IConvertCartEntry, IDeliveryInputAddress } from './types';

export const getOrderTimeslots = {
  queryKey: ['order-timeslots'],
  queryFn: async () => client.queries.orderTimeslots(await _getAuthMode()).then(res => res.data),
};

export const getLegacyUserPaymentInformationQuery = {
  queryKey: ['legacy-user-payment-information'],
  queryFn: async () =>
    client.queries.legacyUserPaymentInformation(await _getAuthMode()).then(res => res.data),
};

export function useCartSubscription(options?: CartOptions) {
  const queryData = useCart(options);

  useEffect(() => {
    // It is posible that the component mounts and unmounts quickly.
    // A race condition can occur where the component unmounts before
    // _getAuthMode() resolves. This will cause the subscription to be
    // created and never properlly dropped.
    // Thus we need to manually track the unmount state and only subscribe
    // if the component is still mounted.
    let componentHasUnmounted = false;

    let unsubscribe = () => {};
    _getAuthMode().then(auth => {
      if (componentHasUnmounted) {
        return;
      }

      const sub = client.subscriptions.cartUpdates(auth).subscribe(cart => {
        queryClient.setQueryData(['cart'], cart, { updatedAt: Date.now() });
      });
      unsubscribe = sub.unsubscribe;
    });

    return () => {
      componentHasUnmounted = true;
      unsubscribe();
    };
  }, []);

  return queryData;
}

export function useCart(options?: CartOptions) {
  const cartServiceIsEnabled = !!useFlag(LaunchDarklyFlag.ENABLE_SIMPLY_BETTER_CART_SERVICE);
  const response = useQuery({
    throwOnError: () => options?.throwOnError ?? false,
    queryKey: ['cart'],
    queryFn: async () => {
      return client.queries
        .cart(await _getAuthMode())
        .then(res => {
          if ('errors' in res) {
            throw res.errors;
          }

          if (!res?.data) {
            throw new Error('UndetectableCartError');
          }
          if (res.data?.id) {
            //Persist the cart in local storage
            WebLocalStorage.setItem(StorageKeys.CART_ID, res.data.id);
          }
          return res.data;
        })
        .catch(_commonErrorHandler);
    },
    enabled: cartServiceIsEnabled,
  });

  // When the service is disabled, the isPending flag will still
  // be set to true, so we override it to force it false if we
  // disable the query.
  return useMemo(() => {
    return cartServiceIsEnabled
      ? {
          cart: response.data,
          isPending: cartServiceIsEnabled && response.isPending,
        }
      : { cart: undefined, isPending: false };
  }, [response, cartServiceIsEnabled]);
}

export function useCartEntryConversion(props: IConvertCartEntry) {
  const data = useQuery({
    queryKey: ['cart-entry-conversion'],
    queryFn: async () => client.queries.convertMenuItemFormat(props).then(res => res.data),
  });

  return data;
}

export const priceCart = {
  mutationFn: async () => {
    return client.mutations
      .priceCart(await _getAuthMode())
      .then(_cartUpdateHandler)
      .catch(_commonErrorHandler);
  },
};

export const commitOrder = {
  mutationFn: async ({ payment }: { payment: Record<string, any> }) => {
    return client.mutations
      .commitCart({ paymentJson: JSON.stringify(payment) }, await _getAuthMode())
      .then(_cartUpdateHandler)
      .catch(_commonErrorHandler);
  },
};

export const selectStore = {
  mutationFn: async ({
    storeNumber,
    serviceMode,
  }: {
    storeNumber: string;
    serviceMode: ServiceMode;
  }) => {
    return client.mutations
      .selectStore(
        {
          storeNumber,
          serviceMode,
        },
        await _getAuthMode()
      )
      .then(_cartUpdateHandler)
      .catch(_commonErrorHandler);
  },
};

export const setDeliveryRestaurant = {
  mutationFn: async ({
    deliveryAddress,
    storeNumber,
    quoteId,
  }: {
    deliveryAddress: IDeliveryInputAddress | null;
    storeNumber: string;
    quoteId: string;
  }) => {
    return client.mutations
      .setDeliveryRestaurant(
        {
          quoteId,
          storeNumber,
          deliveryAddress: JSON.stringify(deliveryAddress),
        },
        await _getAuthMode()
      )
      .then(_cartUpdateHandler)
      .catch(_commonErrorHandler);
  },
};

export const quoteDeliveryOrder = {
  mutationFn: async ({ deliveryAddress }: { deliveryAddress: IDeliveryInputAddress | null }) => {
    return client.mutations
      .quoteDeliveryOrder(
        {
          deliveryAddress: JSON.stringify(deliveryAddress),
        },
        await _getAuthMode()
      )
      .then(_cartUpdateHandler)
      .catch(_commonErrorHandler);
  },
};

export const addOfferToCart = {
  mutationFn: async ({ offerId, entries: _entries }: { offerId: string; entries: any }) => {
    return client.mutations
      .addOfferToCart({ offerId }, await _getAuthMode())
      .then(_cartUpdateHandler)
      .catch(_commonErrorHandler);
  },
};

export const removeOfferfromCart = {
  mutationFn: async ({ offerId }: { offerId: string }) => {
    return client.mutations
      .removeOfferFromCart({ offerId }, await _getAuthMode())
      .then(_cartUpdateHandler)
      .catch(_commonErrorHandler);
  },
};

type Modifiers = Array<{ id: string; quantity: number }>;

export const addItemToCart = {
  mutationFn: async ({
    itemId,
    quantity,
    instructions,
    modifiers,
    isCombo,
  }: {
    itemId: string;
    quantity: number;
    instructions: string;
    modifiers: Modifiers;
    isCombo: boolean;
  }) => {
    return client.mutations
      .addItemToCart(
        {
          itemId,
          quantity,
          instructions,
          modifiersJson: JSON.stringify(modifiers),
          isCombo,
        },
        await _getAuthMode()
      )
      .then(_cartUpdateHandler)
      .catch(_commonErrorHandler);
  },
};

export const updateItemInCart = {
  mutationFn: async ({
    lineId,
    itemId,
    instructions,
    quantity,
    modifiers,
  }: {
    lineId: string;
    itemId?: string;
    instructions?: string;
    quantity?: number;
    modifiers?: Modifiers;
  }) => {
    return client.mutations
      .updateItemInCart(
        {
          lineId,
          instructions,
          itemId,
          quantity,
          modifiersJson: JSON.stringify(modifiers),
        },
        await _getAuthMode()
      )
      .then(_cartUpdateHandler)
      .catch(_commonErrorHandler);
  },
};

export const setDonationAmount = {
  mutationFn: async ({ amount }: { amount: number }) => {
    return client.mutations
      .setDonationAmount(
        {
          amount,
        },
        await _getAuthMode()
      )
      .then(_cartUpdateHandler)
      .catch(_commonErrorHandler);
  },
};

export const setOrderFulfillmentTime = {
  mutationFn: async ({ fulfillmentTime }: { fulfillmentTime: string }) => {
    return client.mutations
      .setOrderFulfillmentTime({ fulfillmentTime }, await _getAuthMode())
      .then(_cartUpdateHandler)
      .catch(_commonErrorHandler);
  },
};

export const usePointsOnItem = {
  mutationFn: async ({ lineId }: { lineId: string }) => {
    return client.mutations
      .usePointsOnItem({ lineId }, await _getAuthMode())
      .then(_cartUpdateHandler)
      .catch(_commonErrorHandler);
  },
};

export const removePointUsageFromItem = {
  mutationFn: async ({ lineId }: { lineId: string }) => {
    return client.mutations
      .removePointUsageFromItem({ lineId }, await _getAuthMode())
      .then(_cartUpdateHandler)
      .catch(_commonErrorHandler);
  },
};

// ----------------------------------------------------------------
// private
// ----------------------------------------------------------------

const _cartUpdateHandler = result => {
  if (result.errors) {
    throw result.errors;
  }
  if (result.data) {
    if (result.data.id) {
      setHeaders(Header.CART_ID, result.data.id);
    }
    queryClient.setQueryData(['cart'], result.data, { updatedAt: Date.now() });
    return result.data;
  }
  throw [new Error('UndetectableCartError')];
};

const _commonErrorHandler = async res => {
  let errors = Array.isArray(res) ? res : [res];

  for (let error of errors) {
    switch (error.message) {
      case 'MissingStoreError':
        router.push({
          pathname: '/store-locator/service-mode',
          params: {
            back: '../',
          },
        });
        // TODO: Blaine - get design advice here
        Toast.show({ title: 'You must select a store before continuing' });
    }
  }

  throw errors;
};

const _getAuthMode = async (): Promise<any> => {
  const session = await getCurrentSession();
  if (session) {
    return {
      authToken: 'Bearer ' + session.getIdToken().getJwtToken(),
      authMode: 'lambda',
    };
  }

  return { authMode: 'apiKey' };
};
